// src/context/AuthContext.js

import React, { createContext, useState, useEffect, useCallback } from 'react';
import Config from "../Config.json";
import { API } from "../api/config";

const API_URL = API.LOGIN;

export const AuthContext = createContext(null);

// Module-level flag — survives React Strict Mode's unmount/remount cycle.
// A useRef() resets when the component remounts, so a second initAuth() run
// slips through the guard. This variable lives outside the component tree.
let _authInitStarted = false;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        setUser(null);
        localStorage.removeItem('appSession');
    }, []);

    const login = useCallback((apiData) => {
        setUser(apiData);
        localStorage.setItem('appSession', JSON.stringify(apiData));
    }, []);

    const getAccessToken = useCallback(() => {
        // Always reads from state first, falls back to storage.
        // No ref needed — we are not trying to silently refresh anywhere.
        try {
            const stored = localStorage.getItem('appSession');
            return stored ? JSON.parse(stored).token || null : null;
        } catch {
            return null;
        }
    }, []);

    const authenticatedFetch = useCallback(async (url, options = {}) => {
        const token = getAccessToken();

        if (!token) {
            logout();
            throw new Error("No token available");
        }

        const res = await fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`,
            },
        });

        if (res.status === 401 || res.status === 403) {
            console.warn(`AuthContext: Received ${res.status} — session invalid, logging out.`);
            logout();
        }

        return res;
    }, [getAccessToken, logout]);

    /**
     * On mount, restore the session from localStorage and validate it
     * against the backend. This is the ONLY auth side-effect on init.
     *
     * Strategy: optimistic restore (fast UI paint) → background validation.
     * If the backend rejects the token, we clear the session cleanly.
     */
    useEffect(() => {
        const initAuth = async () => {
            // Guard against React Strict Mode double-invocation.
            // useRef() resets when Strict Mode unmounts+remounts the component,
            // so the second mount bypasses a ref-based guard. This module-level
            // flag persists across remounts within the same page load.
            if (_authInitStarted) return;
            _authInitStarted = true;

            setLoading(true);

            const storedSession = localStorage.getItem('appSession');

            if (!storedSession) {
                setUser(null);
                setLoading(false);
                return;
            }

            let parsedSession;
            try {
                parsedSession = JSON.parse(storedSession);
            } catch {
                logout();
                setLoading(false);
                return;
            }

            if (!parsedSession?.token) {
                logout();
                setLoading(false);
                return;
            }

            // Snapshot the token we are about to validate. If by the time the
            // server responds localStorage has a DIFFERENT token (because a fresh
            // Google silent sign-in ran concurrently and called login()), we must
            // NOT call logout() — that would wipe the brand-new valid session.
            const tokenBeingValidated = parsedSession.token;

            // 1. Optimistically restore user so the UI paints immediately,
            //    without waiting on the network.
            setUser(parsedSession);

            // 2. Validate the stored token against the server.
            //    We use POST /auth/users/validate-token instead of GET /clients
            //    because the /clients endpoint is behind AWS API Gateway signature
            //    validation that rejects opaque Bearer tokens with 403 before the
            //    Go middleware ever sees them. The validate-token endpoint has no
            //    such gateway-level restriction.
            try {
                const res = await fetch(`${API_URL}/auth/users/validate-token`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${tokenBeingValidated}`,
                    },
                    body: JSON.stringify({ token: tokenBeingValidated }),
                });

                if (res.status === 401 || res.status === 403) {
                    // Token rejected. But only wipe the session if the token in
                    // localStorage is still the same stale one we just validated.
                    // A concurrent login() call (e.g. Google silent sign-in) may
                    // have already stored a fresh valid token — don't clobber it.
                    const currentStored = localStorage.getItem('appSession');
                    let currentToken = null;
                    try { currentToken = JSON.parse(currentStored)?.token; } catch {}

                    if (!currentToken || currentToken === tokenBeingValidated) {
                        console.log("AuthContext: Stored token rejected by server, logging out.");
                        logout();
                    } else {
                        // A fresh login happened while we were validating the old token.
                        // Update state to reflect the new session and stay logged in.
                        console.log("AuthContext: Stale token rejected, but a new session exists — keeping it.");
                        try { setUser(JSON.parse(currentStored)); } catch {}
                    }

                } else if (res.ok) {
                    const data = await res.json();
                    if (!data.success) {
                        console.log("AuthContext: Token validation returned success=false, logging out.");
                        logout();
                    }
                    // On success: keep the already-restored parsedSession as-is.
                }
                // On 5xx / network errors: silently keep the optimistic state.
                // The user stays logged in; they'll get a proper error if they
                // attempt an action. This prevents logouts during backend downtime.

            } catch (networkErr) {
                console.warn("AuthContext: Network error during token validation, keeping cached session.", networkErr);
            }

            setLoading(false);
        };

        initAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            logout,
            getAccessToken,
            authenticatedFetch,
        }}>
            {children}
        </AuthContext.Provider>
    );
};